import { useEffect, useState } from 'react';
import { useContext } from 'react';
import {
    PieChart,
    Series as PieSeries,
    Label,
    Connector,
    Legend as PieLegend,
    Tooltip as PieTooltip,
    Export as PieExport,
} from 'devextreme-react/pie-chart';
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    Paging,
} from "devextreme-react/data-grid";
import { Close as CloseIcon } from "@mui/icons-material";
import { ChartFilterTagBox as TagBox } from './ChartFilterTagBox';
import { Popover } from 'devextreme-react/popover';
import { getLastRefreshTimeFromRawData, handleESignaturePointClick, STATUS_COLOR_MAP } from '../FSServices';
import { createSitesClientApi } from '../../../api-client-nswag/taxportal-client-runtime';
import { convertUTCTimeToLocal } from '../FSServices';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { OidcIdentityContext } from '@rsmus/react-auth';

interface Props {
    rawESignatureData: any[];
    handleRefresh: () => void;
    siteId: number;
}

// ─── Formatters ──────────────────────────────────────────────────────────────

function customizePieTooltip(arg: { argument: string; value: number; percentText: string }) {
    return { text: `${arg.argument}: ${arg.value} (${arg.percentText})` };
}

function customizePieLabel(arg: { argument: string; percentText: string }) {
    return `${arg.argument}: ${arg.percentText}`;
}

function customizePiePoint(pointInfo: { argument?: string }) {
    const status = String(pointInfo?.argument || '');
    return { color: STATUS_COLOR_MAP[status] || '#888b8d' };
}

function customizePieLegendText(arg: any) {
    const name = arg?.pointName || arg?.argument || '';
    const value = arg?.pointValueText || arg?.valueText || arg?.value;
    if (value === undefined || value === null || value === '') return name;
    return `${name} (${value})`;
}

function formatTimeAgo(fromTime: Date, nowMs: number) {
    const diffMs = Math.max(0, nowMs - fromTime.getTime());
    const minutes = Math.floor(diffMs / 60000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;

    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

// ─── Legend Toggle Hook ──────────────────────────────────────────────────────

function usePieLegendSliceToggle() {
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());

    const getLegendStatus = (event: any) => {
        const target = event?.target;
        const status = target?.argument || target?.originalArgument || target?.pointName || target;
        return status !== undefined && status !== null ? String(status) : null;
    };

    const applySelectionToPoints = (nextSelection: Set<string>, component: any) => {
        const series = component?.getAllSeries?.()?.[0];
        if (!series || typeof series.getAllPoints !== 'function') return;

        const hasSelection = nextSelection.size > 0;
        series.getAllPoints().forEach((point: any) => {
            const pointKey = point?.argument !== undefined && point?.argument !== null
                ? String(point.argument)
                : String(point?.originalArgument || '');
            const shouldShow = !hasSelection || nextSelection.has(pointKey);
            shouldShow ? point.show?.() : point.hide?.();
        });
    };

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const status = getLegendStatus(event);
        if (!status) return;

        setSelectedStatuses((prev) => {
            const next = new Set(prev);
            next.has(status) ? next.delete(status) : next.add(status);
            applySelectionToPoints(next, event?.component);
            return next;
        });
    };

    const customizeLegendItems = (items: any[]) =>
        items.map((item) => {
            const itemKey = item.argument !== undefined && item.argument !== null
                ? String(item.argument)
                : String(item.text || item.pointName || item.name || '');
            if (selectedStatuses.size === 0 || selectedStatuses.has(itemKey)) return item;
            return {
                ...item,
                marker: { ...item.marker, opacity: 0.35 },
                font: { ...item.font, color: '#888b8d' },
            };
        });

    return { selectedStatuses, handleLegendClick, customizeLegendItems };
}

// ─── Status Data ─────────────────────────────────────────────────────────────

function getStatusFromRecord(record: any) {
    const candidate = record.fileStatus ? String(record.fileStatus) : '';
    if (candidate.includes('Signed')) return 'Signed';
    if (candidate.includes('Voided')) return 'Voided';
    return 'Pending';
}

function getESignatureStatusFilterValues(rawData: any[]): string[] {
    return Array.from(new Set(rawData.map((record: any) => getStatusFromRecord(record)).filter(Boolean))).sort();
}

function getESignatureStatusData(rawData: any[]) {
    const grouped: Record<string, { value: number; entities: Set<string> }> = {};
    rawData.forEach((record: any) => {
        const status = getStatusFromRecord(record);
        if (!grouped[status]) {
            grouped[status] = { value: 0, entities: new Set<string>() };
        }
        grouped[status].value += 1;
        if (record?.entity) {
            grouped[status].entities.add(String(record.entity));
        }
    });
    return Object.keys(grouped).map((status) => ({
        status,
        value: grouped[status].value,
        entityName: Array.from(grouped[status].entities)[0] || undefined,
    }));
}


export const ESignatureStatusChart = (props: Props) => {

    const { user } = useContext(OidcIdentityContext);
    const rawData = props.rawESignatureData;
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState("");
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);
    const { selectedStatuses, handleLegendClick, customizeLegendItems } = usePieLegendSliceToggle();
    const matchesLegend = (record: any) => selectedStatuses.size === 0 || selectedStatuses.has(getStatusFromRecord(record));
    const statusOptions = getESignatureStatusFilterValues(rawData.filter(matchesLegend));
    const activeFilterCount = (statusFilter.length > 0 ? 1 : 0) + (entityFilter.length > 0 ? 1 : 0);

    const statusFilteredData = statusFilter.length === 0
        ? rawData
        : rawData.filter((record: any) => statusFilter.includes(getStatusFromRecord(record)));
    const entityOptions = Array.from(new Set(statusFilteredData.filter(matchesLegend)
        .map((record: any) => String(record?.entity || record?.entityName || '')).filter(Boolean))).sort();
    const filteredData = entityFilter.length === 0
        ? statusFilteredData
        : statusFilteredData.filter((record: any) => entityFilter.includes(String(record?.entity || record?.entityName || '')));

    const chartData = getESignatureStatusData(filteredData);
    const dataViewRows = filteredData.filter(matchesLegend).map((record: any) => ({
        status: getStatusFromRecord(record),
        entity: String(record?.entity || record?.entityName || ''),
        fileStatus: String(record?.fileStatus || ''),
        fileName: String(record?.fileName || record?.file || ''),
    }));

    useEffect(() => {
        const fetchItemTrack = async () => {
            const itemTrack = await createSitesClientApi.taxportalDbData_GetItemSyncTrackByType(props.siteId, "SYNC_DM", user?.profile.preferred_username as string);
            console.log("Fetched item track esign:", itemTrack);
            const lastModified = (itemTrack as any)?.data?.lastModified ?? (itemTrack as any)?.lastModified
            const localTime = lastModified?.trim() ? convertUTCTimeToLocal(lastModified) : '';
            setLastRefreshTime(localTime);
        };
        fetchItemTrack();
    }, [rawData, props.siteId, user?.profile.preferred_username]);

    return (
        <div>
            <div className="chart-header">
                <div className="chart-title-section1">
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">eSignature - Status Overview</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div
                                onClick={() => setIsDataViewOpen(true)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    border: '1px solid #d0d0d0',
                                    cursor: 'pointer',
                                    backgroundColor: '#fff',
                                }}
                                title="Data Points"
                            >
                                <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                            </div>
                            <div
                                id="esignature-filter-icon-trigger"
                                onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                                className={`fs-chart-filter-trigger${activeFilterCount > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                                title="Filters"
                            >
                                <i className="dx-icon-filter" style={{ fontSize: 16, color: '#555' }} aria-hidden="true"
                                />
                                {activeFilterCount > 0 && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: -4,
                                            right: -4,
                                            minWidth: 16,
                                            height: 16,
                                            borderRadius: 8,
                                            backgroundColor: '#1976d2',
                                            color: '#fff',
                                            fontSize: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0 3px',
                                        }}
                                    >
                                        {activeFilterCount}
                                    </span>
                                )}
                            </div>
                            <div
                                onClick={props.handleRefresh}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    border: '1px solid #d0d0d0',
                                    cursor: 'pointer',
                                    backgroundColor: '#fff',
                                }}
                                title="Refresh"
                            >  <i
                                    className="dx-icon-refresh"
                                    style={{ fontSize: 16, color: '#555' }}
                                    aria-hidden="true"
                                /></div>

                        </div>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>
            </div>

            <Popover
                target="#esignature-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Status</div>
                        <TagBox
                            dataSource={statusOptions}
                            value={statusFilter}
                            onValueChanged={(e) => setStatusFilter(e.value ?? [])}
                            placeholder="All statuses"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={() => {
                                setStatusFilter([]);
                                setEntityFilter([]);
                            }}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <Dialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        borderBottom: "1px solid #e0e0e0",
                    }}
                >

                    <Typography variant="h6">
                        eSignature - Status Overview
                    </Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => setIsDataViewOpen(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 2 }}>
                    <DataGrid
                        dataSource={dataViewRows}
                        height={450}
                        showBorders
                        rowAlternationEnabled
                        columnAutoWidth
                        wordWrapEnabled
                        hoverStateEnabled
                        noDataText="No data available"
                    >
                        <LoadPanel enabled={false} />

                        <FilterRow visible />

                        <HeaderFilter visible />

                        <Paging
                            enabled
                            defaultPageSize={20}
                        />

                        <Scrolling mode="standard" />

                        <Column
                            dataField="status"
                            caption="Status"
                        />

                        <Column
                            dataField="entity"
                            caption="Entity"
                        />

                        <Column
                            dataField="fileStatus"
                            caption="File Status"
                        />

                        <Column
                            dataField="fileName"
                            caption="Information Request"
                        />
                    </DataGrid>
                </DialogContent>
            </Dialog>

            {
                chartData?.length > 0 ? (
                    <PieChart dataSource={chartData} type="doughnut" innerRadius={0.5} resolveLabelOverlapping="shift" customizePoint={customizePiePoint} onLegendClick={handleLegendClick} onPointClick={(event) => handleESignaturePointClick(event, props.siteId)}                    >
                        <PieSeries argumentField="status" valueField="value">
                            <Label visible={true} customizeText={customizePieLabel} radialOffset={-20} font={{ color: '#000000' }} backgroundColor="transparent">
                                <Connector visible={true} width={1} />
                            </Label>
                        </PieSeries>
                        <PieLegend
                            verticalAlignment="bottom"
                            horizontalAlignment="center"
                            itemTextPosition="right"
                            markerSize={14}
                            customizeText={customizePieLegendText}
                            customizeItems={customizeLegendItems}
                        />
                        <PieTooltip enabled={true} customizeTooltip={customizePieTooltip} />
                        <PieExport enabled={false} />
                    </PieChart>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
};
